import React, { useState, useEffect } from 'react';
import { Flag, Plus, X } from 'lucide-react';

const SECTION_COLOR_OPTIONS = ['#00D4AA', '#FF2D78', '#B44DFF', '#FF8A00', '#39FF14', '#4DA6FF', '#FFD700'];

export const SectionMarkerModal = ({ isOpen, onClose, onAddMarker, totalBars, initialStartBar = 0 }) => {
  const [name, setName] = useState('');
  // The box shows ruler numbers (bar 1 is the first bar) and keeps the raw text, so it can be
  // cleared and retyped. Markers store a zero-based startBar.
  const [barText, setBarText] = useState('1');
  const [color, setColor] = useState('#00D4AA');
  const lastBar = Math.max(1, totalBars || 32);
  const typedBar = Number(barText);
  const barValid = /^\d+$/.test(barText) && typedBar >= 1 && typedBar <= lastBar;

  useEffect(() => {
    if (isOpen) setBarText(String(Math.max(0, Math.floor(Number(initialStartBar) || 0)) + 1));
  }, [isOpen, initialStartBar]);

  if (!isOpen) return null;

  const handleAdd = () => {
    if (name.trim() && barValid) {
      onAddMarker?.({ name: name.trim(), startBar: typedBar - 1, color });
      setName('');
      setBarText('1');
      onClose?.();
    }
  };

  return (
    <div data-testid="section-marker-modal" className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div
        className="flex flex-col"
        style={{
          width: 380,
          background: 'var(--bg-card)',
          borderRadius: 16,
          border: '1px solid var(--border)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2">
            <Flag size={18} style={{ color: 'var(--green)' }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Add Section Marker</span>
          </div>
          <button data-testid="close-section-marker-modal" onClick={onClose} className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--bg-elevated)', color: 'var(--text-dim)' }}>
            <X size={14} />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-4 px-5 py-5">
          {/* Name input */}
          <div className="flex flex-col gap-1.5">
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Section Name</label>
            <input
              data-testid="section-marker-name"
              className="outline-none"
              style={{
                height: 36, borderRadius: 8, padding: '0 12px',
                background: 'var(--bg-elevated)', color: 'var(--text)',
                border: '1px solid var(--border)', fontSize: 13,
              }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Intro, Verse, Chorus"
            />
            <div className="flex flex-wrap gap-1">
              {['Intro', 'Verse', 'Pre-Hook', 'Hook', 'Bridge', 'Outro'].map((sectionName) => (
                <button key={sectionName} type="button" onClick={() => setName(sectionName)} style={{ fontSize: 11, padding: '4px 7px', borderRadius: 12, background: name === sectionName ? 'var(--green)' : 'var(--bg-elevated)', color: name === sectionName ? 'var(--bg)' : 'var(--text-secondary)', border: '1px solid var(--border)' }}>{sectionName}</button>
              ))}
            </div>
          </div>

          {/* Start bar */}
          <div className="flex flex-col gap-1.5">
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Start Bar</label>
            <input
              data-testid="section-marker-bar"
              type="text"
              inputMode="numeric"
              aria-invalid={!barValid}
              className="outline-none"
              style={{
                height: 36, borderRadius: 8, padding: '0 12px',
                background: 'var(--bg-elevated)', color: 'var(--text)',
                border: '1px solid var(--border)', fontSize: 13,
              }}
              value={barText}
              onChange={(e) => setBarText(e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, ''))}
            />
            {!barValid && <span data-testid="section-marker-bar-error" style={{ fontSize: 11, color: 'var(--pink)' }}>Pick a bar from 1 to {lastBar}.</span>}
          </div>

          {/* Color picker */}
          <div className="flex flex-col gap-1.5">
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Color</label>
            <div className="flex items-center gap-2">
              {SECTION_COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  data-testid={`section-color-${c.replace('#', '')}`}
                  onClick={() => setColor(c)}
                  className="rounded-full transition-transform"
                  style={{
                    width: 24, height: 24,
                    background: c,
                    border: color === c ? '2px solid var(--text)' : '2px solid transparent',
                    transform: color === c ? 'scale(1.2)' : 'scale(1)',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Add button */}
          <button
            data-testid="add-section-marker-btn"
            onClick={handleAdd}
            disabled={!name.trim() || !barValid}
            className="flex items-center justify-center gap-2"
            style={{
              height: 40, borderRadius: 22,
              fontSize: 13, fontWeight: 600,
              background: 'var(--green)', color: 'var(--bg)',
            }}
          >
            <Plus size={15} />
            Add Marker
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionMarkerModal;
